import React, { useMemo, useState, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useClassName, useWindowSize } from 'common/hooks';
import SearchInput from './SearchInput';
import EventFilters from './EventFilters';
import GeneralEventCard from './GeneralEventCard';
import { SVGIcon } from 'common/components';
import { BottomSheet } from 'public/components/BottomSheet';
import {
  filterEvents,
  groupEventsByMonth,
  hasActiveFilters,
} from '../utils/filters';
import { useEventFilters } from '../hooks';

import './styles/general-events.less';

const GeneralEvents = ({ blueprint, events }) => {
  const classNames = useClassName('GeneralEvents');
  const { isMobile } = useWindowSize();
  const {
    searchInput,
    setSearchInput,
    filters,
    toggleCategory,
    updateDateRange,
    updateCustomDate,
    toggleTimeOfDay,
    resetFilters,
  } = useEventFilters();

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const openFilterDrawer = () => setIsFilterDrawerOpen(true);
  const closeFilterDrawer = () => setIsFilterDrawerOpen(false);

  const filteredEvents = useMemo(
    () => filterEvents(events, { searchInput, filters }),
    [events, searchInput, filters]
  );
  const activeFilters = hasActiveFilters({ searchInput, filters });
  const groupedEvents = useMemo(
    () => groupEventsByMonth(filteredEvents, activeFilters),
    [filteredEvents, activeFilters]
  );

  const filterProps = useMemo(
    () => ({
      events,
      filters,
      onCategoryToggle: toggleCategory,
      onDateRangeChange: updateDateRange,
      onCustomDateChange: updateCustomDate,
      onTimeOfDayToggle: toggleTimeOfDay,
      onResetFilters: resetFilters,
    }),
    [
      events,
      filters,
      toggleCategory,
      updateDateRange,
      updateCustomDate,
      toggleTimeOfDay,
      resetFilters,
    ]
  );
  return (
    <div className={classNames('background')}>
      <div
        className={classNames('container')}
        style={{
          backgroundColor: blueprint.generalBackgroundColors[0],
        }}>
        <div className={classNames('headline')}>
          <div className={classNames('title')}>
            {blueprint.generalSectionTitle}
          </div>
          <SearchInput
            input={searchInput}
            setInput={setSearchInput}
            placeholder="Search by name, category or location"
            events={events}
          />
          {isMobile && (
            <button
              className={classNames('mobile-filters')}
              onClick={openFilterDrawer}>
              <SVGIcon size="tiny" name="mobile-event-filters" />
              <span>Filters</span>
            </button>
          )}
        </div>
        <div
          className={classNames('content')}
          style={{
            backgroundColor: blueprint.generalBackgroundColors[1],
          }}>
          {!isMobile && <EventFilters {...filterProps} variant="desktop" />}
          <div className={classNames('events-container')}>
            {groupedEvents.map(([headerText, monthEvents]) => (
              <div key={headerText} className={classNames('events-group')}>
                <div className={classNames('events-group-date-header')}>
                  {activeFilters
                    ? `Showing ${monthEvents.length} ${
                        monthEvents.length === 1 ? 'event' : 'events'
                      }`
                    : headerText}
                </div>
                {monthEvents.map((event) => (
                  <GeneralEventCard key={event._id} event={event} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      {isMobile && (
        <Suspense fallback={null}>
          <BottomSheet
            isOpen={isFilterDrawerOpen}
            onClose={closeFilterDrawer}
            content={
              <div data-body-scroll-lock-ignore="true">
                <EventFilters
                  {...filterProps}
                  variant="mobile"
                  onApplyMobileFilters={closeFilterDrawer}
                />
              </div>
            }
          />
        </Suspense>
      )}
    </div>
  );
};

GeneralEvents.propTypes = {
  blueprint: PropTypes.object,
  events: PropTypes.array,
  defaultBackgroundColor: PropTypes.bool,
};

export default GeneralEvents;
